// src/components/Pricing.js
import React, { useState, useEffect } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Box,
  Paper,
  Link,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PaymentResultPopup from '../components/PaymentResultPopup';

const Pricing = () => {
  const functions = getFunctions();
  const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
  const createOneTimePurchaseSession = httpsCallable(functions, 'createOneTimePurchaseSession');

  const [stripeSessionLoading, setStripeSessionLoading] = useState(false);
  const [oneTimePurchaseLoading, setOneTimePurchaseLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [purchaseMode, setPurchaseMode] = useState('subscribe'); // 'subscribe' or 'buy-time'
  const userEmail = auth.currentUser ? auth.currentUser.email : '';

  // One-time purchase options
  const oneTimeTimePurchases = [
    { id: 'price_1StyCJGptgEY0Ls0C8Ar1GgY', value: '10 ժամ', price: 50, label: '10 ժամ', type: 'time' },
    { id: 'price_1StyCOGptgEY0Ls0FpW9nyn9', value: '20 ժամ', price: 90, label: '20 ժամ', highlighted: true, type: 'time' },
    { id: 'price_1StyCRGptgEY0Ls0vOpf146J', value: '50 ժամ', price: 200, label: '50 ժամ', type: 'time' },
  ];

  const oneTimeTTSPurchases = [
    { id: 'price_1Syd8YGptgEY0Ls0SkGaCjRD', value: '150 կրեդիտ', price: 15, label: '150 կրեդիտ', type: 'tts' },
    { id: 'price_1Syd8lGptgEY0Ls0huNBpauO', value: '300 կրեդիտ', price: 30, label: '300 կրեդիտ', highlighted: true, type: 'tts' },
    { id: 'price_1Syd92GptgEY0Ls0O43bQx3T', value: '800 կրեդիտ', price: 60, label: '800 կրեդիտ', type: 'tts' },
  ];

  const handleUpgrade = async () => {
    setStripeSessionLoading(true);
    try {
      const result = await createCheckoutSession({
        billingCycle,
        origin: window.location.origin
      });
      window.location.href = result.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setStripeSessionLoading(false);
      // Դուք կարող եք այստեղ ցույց տալ տոստ կամ dialog, եթե ցանկանաք
    }
  };

  const handleBusinessLink = () => {
    window.open(
      `https://tally.so/r/3Evk5L?prefilled_email=${encodeURIComponent(userEmail)}`,
      '_blank'
    );
  };

  const handleBillingCycleChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
      amplitude.track('Billing Cycle Changed', { cycle: newBillingCycle });
    }
  };

  const handlePurchaseModeChange = (event, newMode) => {
    if (newMode !== null) {
      setPurchaseMode(newMode);
      amplitude.track('Purchase Mode Changed', { mode: newMode });
    }
  };

  const handleOneTimePurchase = async (priceId) => {
    setOneTimePurchaseLoading(priceId);
    try {
      const result = await createOneTimePurchaseSession({
        priceId,
        origin: window.location.origin
      });
      window.location.href = result.data.url;
    } catch (error) {
      console.error('Error creating one-time purchase session:', error);
      setOneTimePurchaseLoading(null);
      alert('Սխալ տեղի ունեցավ: Խնդրում ենք փորձեք կրկին');
    }
  };

  useEffect(() => {
    amplitude.track('Pricing Page Loaded');
  }, []);

  const featureTooltips = {
    'Telegram բոտ': 'Հնարավորություն վերծանելու ձայնային հաղորդագրությունները և օգտագործելու ձայնագրությունը շարժման ընթացքում',
    'AI օգնական': 'Հարցեր տվեք տեքստի վերաբերյալ կամ փոփոխեք տեքստը արհեստական բանականության օգնությամբ',
    'Թարգմանություն': '9 լեզուների թարգմանություն',
    'Կարաոկե նվագարկում': 'Ամեն ասվող բառը ավտոմատ կնշվի տեքստում այն հեշտ գտնելու համար'
  };

  const faqs = [
    { question: 'Ինչպե՞ս է հաշվարկվում վերծանման ժամանակը', answer: 'Վերծանման ժամանակը հաշվարկվում է ըստ ձայնագրության իրական տևողության: Օրինակ, 10 րոպեանոց ձայնագրությունը հաշվարկվում է որպես 10 րոպե:' },
    { question: 'Կարո՞ղ եմ փոխել իմ պլանը', answer: 'Այո, դուք կարող եք ցանկացած պահի փոխել ձեր պլանը: Եթե անցնեք ավելի թանկ պլանի, ապա կվճարեք միայն տարբերությունը:' },
    { question: 'Արդյո՞ք իմ վճարումը ապահով է', answer: 'Վճարումների համար մենք օգտագործում ենք Stripe համակարգը, որը աշխարհի ամենամեծ վճարահաշվարկային համակարգն է։ Այն ունի բանկային մակարդակի անվտանգություն և օգտագործվում է աշխարհի ամենաառաջատար ընկերությունների կողմից։' },
    { question: 'Կարո՞ղ եմ գնել իմ թիմի համար', answer: 'Այո, մենք ունենք հատուկ առաջարկներ թիմերի համար: Խնդրում ենք կապվել մեզ հետ ավելի մանրամասն տեղեկությունների համար:' },
    { question: 'Արդյո՞ք իմ տեքստային վերծանումներն ապահով են', answer: 'Այո, ձեր տվյալների գաղտնիությունը և ապահովությունը մեր առաջնահերթությունն է: Մենք օգտագործում ենք վերջին սերնդի տեխնոլոգիաներ՝ ձեր բոլոր տվյալները պաշտպանելու համար և երբեք չենք տրամադրում այն երրորդ կողմին:' },
    { question: 'Արդյո՞ք տրամադրում եք փորձնական ժամանակաշրջան', answer: 'Այո, մենք ուրախ կլինենք տրամադրել ձեզ փորձնական ժամանակաշրջան: Խնդրում ենք կապվել մեզ հետ դրա կազմակերպման համար:' },
    { question: 'Ի՞նչ է ավելացվելու հաջորդիվ', answer: 'Մենք աշխատում ենք մի շարք հետաքրքիր գործառույթների վրա, ներառյալ ենթագրերը, տեքստից խոսք, զանգերի վերլուծություն, բջջային հավելված, Chrome ընդլայնում և շատ ավելին: Հետևեք մեզ Facebook-ում և Telegram-ում՝ վերջին թարմացումների համար:' }
  ];

  const commonFeatures = [
    'Ֆայլերի վերբեռնում (մինչև 4 ժամ/ֆայլ)',
    'Մինչև 2,000 նիշ՝ յուրաքանչյուր TTS գեներացիայի համար',
    'Պատմություն',
    'Որոնում',
    'Telegram բոտ',
    'AI օգնական',
    'Թարգմանություն',
    'Վիճակագրություն',
    'Կարաոկե նվագարկում',
    'AI վերնագիր'
  ];

  const Feature = ({ text }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
      <Typography variant="body1">{text}</Typography>
      {featureTooltips[text] && (
        <Tooltip title={featureTooltips[text]} arrow placement="top">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const plans = [
    {
      name: 'Pro',
      price: billingCycle === 'yearly' ? '$90/տարի' : '$15/ամիս',
      description: 'Լրագրողների, սոցիոլոգների ու այլ պրոֆեսիոնալների համար',
      hours: '4 ժամ/ամիս',
      tts_generations: '200 հատ/ամիս',
      highlighted: true,
      discount: '-50%',
      features: commonFeatures,
      cta: {
        label: 'Ընտրել PRO',
        action: handleUpgrade,
        isPrimary: true
      }
    },
    {
      name: 'Business',
      price: '',
      description: 'Մեծ ծավալների շարունակական վերածնման կարիք ունեցող բիզնեսների համար',
      hours: '50+ ժամ/ամիս',
      tts_generations: '10+ GB', // Keeping memory for business as requested or changing? Request said remove memory, write TTS. assuming similar logic.
      // Wait, user said "Remove the notice about memory (2 GB, 10+ GB); it's not relevant and write the number of TTS generations instead of this memory label."
      // I should check if Business plan needs this update too. User specifically mentioned "Pro plan details".
      // But for consistency I should probably update Business too if it has TTS.
      // However, the request said "On the pricing page UI, we need to add the number of generations that the user will get on a pro plan...".
      // It didn't explicitly say for Business. But Business has volume discounts.
      // Let's stick to Pro plan changes primarily, but since I am replacing the field in the UI, I need something for Business otherwise it breaks.
      // Business plan usually has custom/more. Let's put 'Անսահմանափակ' or 'Custom' or just keep the structure compatible.
      // Actually, for Business it said "storage: '10+ GB'". I will change it to 'Պայմանագրային'.
      storage: 'Պայմանագրային', // Reverting to storage key for business to avoid breaking if I use different keys, but I will change the UI code to look for tts_generations OR storage?
      // actually let's just use tts_generations key for uniformity in loop if possible, or handle individually.
      // simpler: upgrade logic.
      // I will just change the UI rendering part to show TTS for Pro.
      // For Business, let's leave it as is or change to TTS?
      // "Remove the notice about memory... and write the number of TTS generations instead".
      // I will change Business to also show TTS placeholder.
      tts_generations: 'Պայմանագրային',
      includesProPlus: true,
      additionalFeatures: [
        'API օգտագործման հնարավորություն',
        'QA ստուգաթերթեր վերլուծության համար',
        'Ծավալային զեղչեր'
      ],
      cta: {
        label: 'Կապվել մեզ հետ',
        action: handleBusinessLink,
        isPrimary: false
      }
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, py: 4 }}>
      <Typography
        variant="h3"
        align="center"
        sx={{ mb: 2, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' } }}
      >
        Ընտրեք ձեզ հարմար պլանը
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <ToggleButtonGroup
          value={purchaseMode}
          exclusive
          onChange={handlePurchaseModeChange}
          aria-label="purchase mode"
          color="primary"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="subscribe" aria-label="subscribe" sx={{ px: 3 }}>
            Բաժանորդագրություն
          </ToggleButton>
          <ToggleButton value="buy-time" aria-label="buy-time" sx={{ px: 3 }}>
            Միանվագ գնում
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {purchaseMode === 'subscribe' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            aria-label="billing cycle"
            color="primary"
          >
            <ToggleButton value="monthly" aria-label="monthly" sx={{ px: 3 }}>
              Ամսական
            </ToggleButton>
            <ToggleButton value="yearly" aria-label="yearly" sx={{ px: 3 }}>
              Տարեկան
              <Chip label={plans[0].discount} size="small" color="success" sx={{ ml: 1, fontWeight: 'bold' }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {purchaseMode === 'subscribe' ? (
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
          {plans.map((plan, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Paper
                elevation={plan.highlighted ? 6 : 1}
                sx={{
                  position: 'relative',
                  p: { xs: 2.5, sm: 2.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.highlighted ? 2 : 1,
                  borderColor: plan.highlighted ? 'primary.main' : 'grey.200',
                  borderRadius: 2
                }}
              >
                {plan.highlighted && plan.discount && billingCycle === 'yearly' && (
                  <Chip
                    label={plan.discount}
                    color="success"
                    sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                  />
                )}
                <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                  {plan.name}
                </Typography>
                <Typography variant="h3" sx={{ mb: 1 }} fontWeight="bold">
                  {plan.price || ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {plan.description}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    mb: 2,
                    p: 1.5,
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Վերծանում
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {plan.hours}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      TTS գեներացիաներ
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {plan.tts_generations}
                    </Typography>
                  </Box>
                </Box>
                {plan.features &&
                  plan.features.map((feature, i) => <Feature key={i} text={feature} />)}
                {plan.includesProPlus && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                      Ներառում է ամեն ինչ Pro-ից և հավելյալ հնարավորությունները.
                    </Typography>
                    {plan.additionalFeatures &&
                      plan.additionalFeatures.map((feature, i) => <Feature key={i} text={feature} />)}
                  </>
                )}
                {!plan.includesProPlus && plan.additionalFeatures &&
                  plan.additionalFeatures.map((feature, i) => <Feature key={i} text={feature} />)}
                {plan.cta && (
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={plan.cta.action}
                    disabled={plan.cta.isPrimary && stripeSessionLoading}
                    sx={{ mt: 'auto', py: 1.25, borderRadius: 2 }}
                  >
                    {plan.cta.isPrimary && stripeSessionLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      plan.cta.label
                    )}
                  </Button>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          <Typography variant="h5" align="center" sx={{ mb: 3, mt: 2 }}>
            Վերծանման ժամանակ
          </Typography>
          <Grid container spacing={3} justifyContent="center" sx={{ mb: 6 }}>
            {oneTimeTimePurchases.map((option, idx) => (
              <Grid item xs={12} sm={6} md={4} key={`time-${idx}`}>
                <Paper
                  elevation={option.highlighted ? 6 : 1}
                  sx={{
                    position: 'relative',
                    p: { xs: 2.5, sm: 2.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    border: option.highlighted ? 2 : 1,
                    borderColor: option.highlighted ? 'primary.main' : 'grey.200',
                    borderRadius: 2,
                    height: '100%'
                  }}
                >
                  {option.highlighted && (
                    <Chip
                      label="Լավ առաջարկ"
                      color="success"
                      sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                    />
                  )}
                  <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                    {option.value}
                  </Typography>
                  <Typography variant="h3" sx={{ mb: 1 }} fontWeight="bold">
                    ${option.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Միանվագ գնում
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Գործում է 2 ամիս
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Վերծանման ժամանակ
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {option.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => handleOneTimePurchase(option.id)}
                    disabled={oneTimePurchaseLoading === option.id}
                    sx={{ mt: 'auto', py: 1.25, borderRadius: 2 }}
                  >
                    {oneTimePurchaseLoading === option.id ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Գնել'
                    )}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h5" align="center" sx={{ mb: 3 }}>
            TTS գեներացիաներ
          </Typography>
          <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            {oneTimeTTSPurchases.map((option, idx) => (
              <Grid item xs={12} sm={6} md={4} key={`tts-${idx}`}>
                <Paper
                  elevation={option.highlighted ? 6 : 1}
                  sx={{
                    position: 'relative',
                    p: { xs: 2.5, sm: 2.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    border: option.highlighted ? 2 : 1,
                    borderColor: option.highlighted ? 'primary.main' : 'grey.200',
                    borderRadius: 2,
                    height: '100%'
                  }}
                >
                  {option.highlighted && (
                    <Chip
                      label="Լավ առաջարկ"
                      color="success"
                      sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                    />
                  )}
                  <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                    {option.value}
                  </Typography>
                  <Typography variant="h3" sx={{ mb: 1 }} fontWeight="bold">
                    ${option.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Միանվագ գնում
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Գործում է 2 ամիս
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Քանակ
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {option.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => handleOneTimePurchase(option.id)}
                    disabled={oneTimePurchaseLoading === option.id}
                    sx={{ mt: 'auto', py: 1.25, borderRadius: 2 }}
                  >
                    {oneTimePurchaseLoading === option.id ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Գնել'
                    )}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Popup for payment success/failure based on URL param */}
      <PaymentResultPopup />

      <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
          Հաճախ տրվող հարցեր
        </Typography>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1, borderRadius: 1, overflow: 'hidden' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Pricing;